import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, Search, SlidersHorizontal, X } from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { parseSkillFacets } from "../utils/facets";
import { FILTRO_VAZIO, type SkillsFilterMode, type SkillsFilterState } from "../utils/filter";
import type { SkillStorefrontEntry } from "../types/skill";

interface SkillsToolbarProps {
  /** A vitrine inteira: é dela que saem as etiquetas oferecidas no filtro. */
  skills: SkillStorefrontEntry[];
  value: SkillsFilterState;
  onChange: (proximo: SkillsFilterState) => void;
  /** Quantas sobraram depois do filtro, para dizer quando nada sobrou. */
  total: number;
}

/**
 * Busca e filtros do catálogo de Skills (AB#85634).
 *
 * Compacta e numa linha só: ela fica acima da grade em três telas diferentes, e
 * uma barra alta empurraria os cartões para fora da primeira dobra justamente
 * onde a pessoa foi procurar alguma coisa.
 */
export const SkillsToolbar = ({ skills, value, onChange, total }: SkillsToolbarProps) => {
  const { t } = useTranslation();

  /**
   * As etiquetas oferecidas saem do catálogo, e não de um cadastro.
   *
   * Assim o filtro nunca oferece uma opção que não devolve nada — o que é o
   * defeito mais irritante de um filtro, e o mais comum quando ele lê de uma
   * lista separada do que está na tela.
   */
  const grupos = useMemo(() => {
    const porNome = new Map<string, Map<string, string>>();

    for (const skill of skills) {
      for (const etiqueta of parseSkillFacets(skill.facets)) {
        const valores = porNome.get(etiqueta.name) ?? new Map<string, string>();
        valores.set(etiqueta.raw, etiqueta.value);
        porNome.set(etiqueta.name, valores);
      }
    }

    return [...porNome.entries()]
      .map(([nome, valores]) => ({
        nome,
        valores: [...valores.entries()]
          .map(([raw, valor]) => ({ raw, valor }))
          .sort((a, b) => a.valor.localeCompare(b.valor)),
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [skills]);

  const alternarFacet = (raw: string) => {
    const proximas = value.facets.includes(raw)
      ? value.facets.filter((escolhida) => escolhida !== raw)
      : [...value.facets, raw];

    onChange({ ...value, facets: proximas });
  };

  const modos: Array<{ id: SkillsFilterMode; rotulo: string }> = [
    { id: "all", rotulo: t("skills.filter.all") },
    { id: "store", rotulo: t("skills.filter.store") },
    { id: "installed", rotulo: t("skills.filter.installed") },
  ];

  const limpo = value.query === "" && value.mode === "all" && value.facets.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          {/* `z-10` porque o Input embrulha o campo num div próprio, e o input
              tem fundo opaco: sem a camada, o ícone fica ATRÁS dele. O botão de
              limpar não precisa — vem depois no DOM. */}
          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={value.query}
            onChange={(event) => onChange({ ...value, query: event.target.value })}
            placeholder={t("skills.filter.searchPlaceholder")}
            className="h-9 pl-9 pr-9"
          />
          {value.query && (
            <button
              type="button"
              aria-label={t("skills.filter.clearSearch")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              onClick={() => onChange({ ...value, query: "" })}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Grupo segmentado à mão: o produto não tem um toggle group, e trazer
            um componente novo para três botões custaria mais do que resolve. */}
        <div className="flex h-9 shrink-0 items-center rounded-md border bg-muted/40 p-0.5">
          {modos.map((modo) => (
            <button
              key={modo.id}
              type="button"
              onClick={() => onChange({ ...value, mode: modo.id })}
              className={cn(
                "rounded-[5px] px-3 py-1 text-xs font-medium transition-colors",
                value.mode === modo.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {modo.rotulo}
            </button>
          ))}
        </div>

        <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 shrink-0 gap-1.5">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                {t("skills.filter.facets")}
                {value.facets.length > 0 && (
                  <Badge variant="secondary" className="ml-0.5 h-5 px-1.5 text-[11px]">
                    {value.facets.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>

            <PopoverContent align="end" className="w-64 p-0">
              {/* Rolagem no próprio contêiner, e não com `ScrollArea`: o
                  viewport do Radix é `h-full`, e `max-h` na raiz não lhe dá
                  altura de referência — a lista crescia para fora do popover
                  sem nunca rolar. */}
              <div className="max-h-[320px] overflow-y-auto p-2">
                {grupos.map((grupo) => (
                  <div key={grupo.nome} className="mb-2 last:mb-0">
                    <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {grupo.nome}
                    </p>
                    {grupo.valores.map((valor) => {
                      const marcada = value.facets.includes(valor.raw);

                      return (
                        <button
                          key={valor.raw}
                          type="button"
                          onClick={() => alternarFacet(valor.raw)}
                          className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                        >
                          <span className="truncate">{valor.valor}</span>
                          {marcada && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </PopoverContent>
        </Popover>

        {!limpo && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 shrink-0 text-muted-foreground"
            onClick={() => onChange(FILTRO_VAZIO)}
          >
            {t("skills.filter.clear")}
          </Button>
        )}
      </div>

      {/* As etiquetas escolhidas ficam VISÍVEIS fora do popover: dentro dele,
          quem fechasse a lista não teria como saber por que a grade encolheu. */}
      {value.facets.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {value.facets.map((raw) => {
            const [nome, valor] = raw.split(":");

            return (
              <Badge
                key={raw}
                variant="secondary"
                className="cursor-pointer gap-1 text-[11px] font-normal"
                onClick={() => alternarFacet(raw)}
              >
                <span className="text-muted-foreground">{nome}:</span>
                {valor}
                <X className="h-3 w-3" />
              </Badge>
            );
          })}
        </div>
      )}

      {total === 0 && !limpo && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {t("skills.filter.noResults")}
        </p>
      )}
    </div>
  );
};
